import { createIdempotencyKey, get, post, put } from './client';
import { MOCK_CREDIT_CARDS, REAL_API_ENABLED } from './flags';
import * as mock from '@/mock/handlers/creditCards';
import type { CardApplication, CreditCard } from '@/types/phase4.types';

export type CreditCardControllerInput = Record<string, unknown>;
export type CreditCardControllerResult = Record<string, unknown>;

export interface CreditCardLockResult {
  id: string;
  maskedNumber: string;
  variantId: string;
  network: string;
  expiryDate: string;
  limitPaise: number;
  locked: boolean;
  active: boolean;
  applicationStatus: string;
}

export interface CreditCardEligibilityInput extends CreditCardControllerInput {
  variantId: string;
  bureauConsent: boolean;
  employmentType: string;
  monthlyIncomePaise: number;
  employer: string;
  existingEmiPaise: number;
}

export interface CreditCardApplicationInput extends CreditCardControllerInput {
  variantId: string;
  network: string;
  deliveryAddress: string;
  limitPaise: number;
  addOn: boolean;
}

export interface CreditCardActivationInput {
  pin: string;
}

const creditCardPath = (id: string): string => `/credit-cards/${encodeURIComponent(id)}`;

const unavailable = (): never => { throw new Error('Credit Cards backend not available yet. This build runs on the mock.'); };
export const catalogue = () => MOCK_CREDIT_CARDS ? mock.catalogue() : unavailable();
export const networkChoiceEnabled = (): boolean => MOCK_CREDIT_CARDS ? mock.networkChoiceEnabled() : false;
export const applications = (): Promise<CardApplication[]> => MOCK_CREDIT_CARDS ? mock.applications() : unavailable();
export const cards = (): Promise<CreditCard[]> => MOCK_CREDIT_CARDS ? mock.cards() : unavailable();
export const apply = (input: Parameters<typeof mock.apply>[0]): Promise<CardApplication> => MOCK_CREDIT_CARDS ? mock.apply(input) : unavailable();
export const activate = (id: string): Promise<CreditCard> => MOCK_CREDIT_CARDS ? mock.activate(id) : unavailable();
export const setChannel = (id: string, channel: keyof CreditCard['channels'], enabled: boolean): Promise<void> => MOCK_CREDIT_CARDS ? mock.setChannel(id, channel, enabled) : unavailable();
export const setLocked = (id: string, locked: boolean): Promise<void> => MOCK_CREDIT_CARDS ? mock.setLocked(id, locked) : unavailable();
export const linkUpi = (id: string): Promise<void> => MOCK_CREDIT_CARDS ? mock.linkUpi(id) : unavailable();
export const changeNetwork = (id: string, network: string): Promise<void> => MOCK_CREDIT_CARDS ? mock.changeNetwork(id, network) : unavailable();
export const close = (id: string): Promise<void> => MOCK_CREDIT_CARDS ? mock.close(id) : unavailable();
export const payBill = (id: string, amountRupees: number): Promise<void> => MOCK_CREDIT_CARDS ? mock.payBill(id, amountRupees) : unavailable();
export const changeBillingDay = (id: string, day: number): Promise<void> => MOCK_CREDIT_CARDS ? mock.changeBillingDay(id, day) : unavailable();
export const convertToEmi = (id: string, txnId: string, months: number): Promise<void> => MOCK_CREDIT_CARDS ? mock.convertToEmi(id, txnId, months) : unavailable();

export const convertTransactionToEmi = (cardId: string, transactionId: string, tenureMonths: number): Promise<Record<string, unknown>> =>
  REAL_API_ENABLED
    ? post<Record<string, unknown>>(
        `/credit-cards/${encodeURIComponent(cardId)}/transactions/${encodeURIComponent(transactionId)}/emi`,
        undefined,
        { params: { tenureMonths } },
      )
    : unavailable();
export const resetPin = (id: string): Promise<void> => MOCK_CREDIT_CARDS ? mock.resetPin(id) : unavailable();

export const lockCreditCard = (id: string, locked: boolean): Promise<CreditCardLockResult> =>
  put<CreditCardLockResult>(`${creditCardPath(id)}/lock`, undefined, { params: { locked } });

export const payWithCreditCardViaUpi = (
  id: string,
  input: CreditCardControllerInput,
  idempotencyKey = createIdempotencyKey(),
): Promise<CreditCardControllerResult> =>
  post<CreditCardControllerResult>(`${creditCardPath(id)}/upi-pay`, input, {
    headers: { 'Idempotency-Key': idempotencyKey },
  });

export const revealCreditCard = (id: string): Promise<CreditCardControllerResult> =>
  post<CreditCardControllerResult>(`${creditCardPath(id)}/reveal`);

export const payCreditCardBill = (
  id: string,
  input: CreditCardControllerInput,
  idempotencyKey = createIdempotencyKey(),
): Promise<CreditCardControllerResult> =>
  post<CreditCardControllerResult>(`${creditCardPath(id)}/pay-bill`, input, {
    headers: { 'Idempotency-Key': idempotencyKey },
  });

export const blockCreditCard = (id: string): Promise<CreditCardLockResult> =>
  post<CreditCardLockResult>(`${creditCardPath(id)}/block`);

export const activateCreditCard = (
  id: string,
  input: CreditCardActivationInput,
): Promise<CreditCardLockResult> =>
  post<CreditCardLockResult>(`${creditCardPath(id)}/activate`, input);

export const checkCreditCardEligibility = (
  input: CreditCardEligibilityInput,
): Promise<CreditCardControllerResult> =>
  post<CreditCardControllerResult>('/credit-cards/eligibility', input);

export const createCreditCardApplication = (
  input: CreditCardApplicationInput,
): Promise<CreditCardControllerResult> =>
  post<CreditCardControllerResult>('/credit-cards/applications', input);

export const getCreditCardStatement = (id: string): Promise<CreditCardControllerResult[]> =>
  get<CreditCardControllerResult[]>(`${creditCardPath(id)}/statement`);

export const getCreditCardSnapshot = (): Promise<CreditCardControllerResult> =>
  get<CreditCardControllerResult>('/credit-cards/snapshot');

export const getCreditCardApplication = (id: string): Promise<CreditCardControllerResult> =>
  get<CreditCardControllerResult>(`/credit-cards/applications/${encodeURIComponent(id)}`);
